import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { Button, Skeleton } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import {
    LayoutDashboard, Users, UsersRound, LogIn, Smartphone, Apple, CreditCard,
    IndianRupee, BadgeCheck, XCircle, Gift, RefreshCw, CalendarDays, CalendarRange,
    ArrowLeft, ArrowRight, ChevronDown, Check, SlidersHorizontal, Inbox, AlertTriangle,
} from 'lucide-react';
import Calling from '../api/Calling';
import getISTDate from '../components/getISTDate';
import UserChart from '../components/charts/UserChart';
import LoginChart from '../components/charts/LoginChart';
import PlanHistoryChart from '../components/charts/Planhistorychart';
import ReferralChart from '../components/charts/ReferralChart';

// Brand palette
const BRAND = '#34C759';
const BRAND_HOVER = '#2db84d';
const BRAND_SOFT = 'rgba(52,199,89,0.10)';
const BRAND_RING = 'rgba(52,199,89,0.45)';

// Premium green-accent outlined field styling for the date pickers
const dateFieldSx = {
    minWidth: 168,
    '& .MuiInputLabel-root': { fontSize: '0.8rem', fontWeight: 500, color: '#6b7280' },
    '& .MuiInputLabel-root.Mui-focused': { color: BRAND },
    '& .MuiOutlinedInput-root': {
        borderRadius: '12px',
        backgroundColor: '#fff',
        fontSize: '0.875rem',
        fontWeight: 600,
        color: '#111827',
        transition: 'border-color .15s ease, box-shadow .15s ease',
        '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
        '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: BRAND_RING },
        '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: BRAND, borderWidth: '1.5px' },
        '&.Mui-focused': { boxShadow: `0 0 0 4px ${BRAND_SOFT}` },
    },
    '& .MuiInputAdornment-root .MuiIconButton-root': {
        color: '#9ca3af',
        borderRadius: '8px',
        transition: 'color .15s ease, background-color .15s ease',
        '&:hover': { color: BRAND, backgroundColor: BRAND_SOFT },
    },
};
const applyBtnSx = {
    backgroundColor: BRAND,
    borderRadius: '12px',
    textTransform: 'none',
    fontWeight: 600,
    boxShadow: '0 6px 16px -6px rgba(52,199,89,0.6)',
    whiteSpace: 'nowrap',
    px: 2.75,
    height: 40,
    '&:hover': { backgroundColor: BRAND_HOVER, boxShadow: '0 8px 20px -6px rgba(52,199,89,0.7)' },
};

// Premium, fully-branded theme for the calendar popup (keys verified for @mui/x-date-pickers@9.6).
const pickerTheme = createTheme({
    palette: { primary: { main: BRAND } },
    shape: { borderRadius: 12 },
    typography: { fontWeightMedium: 600 },
    components: {
        MuiPickerPopper: {
            styleOverrides: {
                paper: {
                    borderRadius: 16,
                    marginTop: 6,
                    border: '1px solid #eef0f2',
                    boxShadow: '0 18px 50px -12px rgba(16,24,40,0.22), 0 8px 16px -8px rgba(16,24,40,0.10)',
                    overflow: 'hidden',
                },
            },
        },
        MuiPickersCalendarHeader: {
            styleOverrides: {
                root: { paddingLeft: 18, paddingRight: 14, marginTop: 12, marginBottom: 4 },
                label: { fontWeight: 700, fontSize: '0.95rem', color: '#111827' },
                switchViewButton: { color: '#374151', '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiPickersArrowSwitcher: {
            styleOverrides: {
                button: { color: '#6b7280', borderRadius: 10, '&:hover': { backgroundColor: BRAND_SOFT, color: BRAND } },
            },
        },
        MuiDayCalendar: {
            styleOverrides: {
                weekDayLabel: { fontWeight: 700, fontSize: '0.72rem', color: '#9ca3af', textTransform: 'uppercase' },
            },
        },
        MuiPickerDay: {
            styleOverrides: {
                root: {
                    fontWeight: 600,
                    borderRadius: 10,
                    transition: 'background-color .12s ease, color .12s ease',
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&:focus': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': {
                        backgroundColor: BRAND,
                        color: '#fff',
                        boxShadow: '0 4px 10px -3px rgba(52,199,89,0.6)',
                        '&:hover': { backgroundColor: BRAND_HOVER },
                        '&:focus': { backgroundColor: BRAND_HOVER },
                    },
                },
                today: {
                    border: `1.5px solid ${BRAND}`,
                    color: BRAND,
                    fontWeight: 700,
                    '&:not(.Mui-selected)': { backgroundColor: BRAND_SOFT },
                },
            },
        },
        MuiYearCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
        MuiMonthCalendar: {
            styleOverrides: {
                button: {
                    borderRadius: 10,
                    fontWeight: 600,
                    '&:hover': { backgroundColor: BRAND_SOFT },
                    '&.Mui-selected': { backgroundColor: BRAND, color: '#fff', '&:hover': { backgroundColor: BRAND_HOVER }, '&:focus': { backgroundColor: BRAND_HOVER } },
                },
            },
        },
    },
});

const pickerSlots = {
    openPickerIcon: () => <CalendarDays size={18} />,
    leftArrowIcon: () => <ArrowLeft size={18} />,
    rightArrowIcon: () => <ArrowRight size={18} />,
    switchViewIcon: () => <ChevronDown size={18} />,
};

const QUICK_RANGES = [
    { key: 'today', label: 'Today' },
    { key: 'yesterday', label: 'Yesterday' },
    { key: 'thisWeek', label: 'This Week' },
    { key: 'thisMonth', label: 'This Month' },
    { key: 'last6Months', label: 'Last 6 Months' },
    { key: 'thisYear', label: 'This Year' },
];

const DATA_TYPES = [
    { key: 'all', label: 'All' },
    { key: 'users', label: 'Users' },
    { key: 'login', label: 'Logins' },
    { key: 'planHistory', label: 'Plan History' },
    { key: 'referral', label: 'Referral' },
    { key: 'referralPlan', label: 'Referral Plan' },
];

const Home = () => {
    const [loader, setLoader] = useState(false);
    const [userData, setUserData] = useState([]);
    const [loginData, setLogin] = useState([]);
    const [androidLoginData, setAndroidLoginData] = useState([]);
    const [iosLoginData, setIosLoginData] = useState([]);
    const [planHistoryData, setPlanHistoryData] = useState([]);
    const [activePlans, setActivePlans] = useState([]);
    const [expiredPlans, setExpiredPlans] = useState([]);

    // States for referral and delete request
    const [referralData, setReferralData] = useState([]);
    const [referralPlanData, setReferralPlanData] = useState([]);
    const [activeReferralPlans, setActiveReferralPlans] = useState([]);
    const [expiredReferralPlans, setExpiredReferralPlans] = useState([]);
    const [deleteRequestCount, setDeleteRequestCount] = useState(0);

    const [startDate, setStartDate] = useState(getISTDate());
    const [endDate, setEndDate] = useState(getISTDate());
    // Draft (uncommitted) date-range values for the pickers — applied only on "Apply" click
    const [draftStart, setDraftStart] = useState(dayjs(getISTDate()));
    const [draftEnd, setDraftEnd] = useState(dayjs(getISTDate()));

    const [selectedBtn, setSelectedBtn] = useState('today');
    const [selectShowType, setSelectShowType] = useState('users');
    const [dataFetchError, setDataFetchError] = useState(null);

    const [lastUpdated, setLastUpdated] = useState(new Date());
    const [isRefreshing, setIsRefreshing] = useState(false);

    const navigate = useNavigate();

    const fetchData = async () => {
        setLoader(true);
        setDataFetchError(null);

        try {
            let dataTypes;
            if (selectShowType === 'all') {
                dataTypes = 'users,login,planHistory,referral,referralPlan';
            } else {
                dataTypes = selectShowType;
            }

            const response = await Calling.adminDashbord(startDate, endDate, dataTypes);

            if (response?.status) {
                setUserData(response?.totalUser || []);
                const totalLogin = response?.totalLogin || [];
                setLogin(totalLogin);

                const androidLogins = totalLogin.filter(login => login.l_platform === 'android');
                const iosLogins = totalLogin.filter(login => login.l_platform === 'ios');
                setAndroidLoginData(androidLogins);
                setIosLoginData(iosLogins);

                const planHistory = response?.planHistory || [];
                setPlanHistoryData(planHistory);

                const active = planHistory.filter(plan => plan.status === 'active');
                const expired = planHistory.filter(plan => plan.status === 'expired');
                setActivePlans(active);
                setExpiredPlans(expired);

                setReferralData(response?.totalReferral || []);

                const referralPlans = response?.totalReferralPlan || [];
                setReferralPlanData(referralPlans);

                const activeRefPlans = referralPlans.filter(plan => plan.status === 'active');
                const expiredRefPlans = referralPlans.filter(plan => plan.status === 'expired');
                setActiveReferralPlans(activeRefPlans);
                setExpiredReferralPlans(expiredRefPlans);

                setDeleteRequestCount(response?.deleteAccount || 0);

                setLastUpdated(new Date());
            } else {
                toast.error(response?.message || 'Failed to fetch data');
                setDataFetchError(response?.message || 'Failed to fetch data');
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to fetch dashboard data');
            setDataFetchError('Failed to fetch dashboard data');
        } finally {
            setLoader(false);
            setIsRefreshing(false);
        }
    };

    const handleRefresh = async () => {
        setIsRefreshing(true);
        await fetchData();
    };

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate, selectShowType]);

    // Keep the picker drafts in sync whenever the committed range changes
    useEffect(() => {
        setDraftStart(startDate ? dayjs(startDate) : null);
        setDraftEnd(endDate ? dayjs(endDate) : null);
    }, [startDate, endDate]);

    const handleDateRangeFilter = (range) => {
        const todayUTC = new Date();

        const toIST = (date) => {
            const istOffset = 5.5 * 60 * 60 * 1000;
            const istTime = new Date(date.getTime() + istOffset);
            return istTime.toISOString().split('T')[0];
        };

        let start, end;

        switch (range) {
            case 'today':
                start = toIST(todayUTC);
                end = start;
                setSelectedBtn('today');
                break;

            case 'yesterday':
                const yesterdayUTC = new Date(todayUTC);
                yesterdayUTC.setDate(todayUTC.getDate() - 1);
                start = toIST(yesterdayUTC);
                end = start;
                setSelectedBtn('yesterday');
                break;

            case 'thisWeek':
                const firstDayOfWeekUTC = new Date(todayUTC);
                firstDayOfWeekUTC.setDate(todayUTC.getDate() - todayUTC.getDay());
                const lastDayOfWeekUTC = new Date(firstDayOfWeekUTC);
                lastDayOfWeekUTC.setDate(firstDayOfWeekUTC.getDate() + 6);

                start = toIST(firstDayOfWeekUTC);
                end = toIST(lastDayOfWeekUTC);
                setSelectedBtn('thisWeek');
                break;

            case 'thisMonth':
                const firstDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth(), 1);
                const lastDayOfMonthUTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayOfMonthUTC);
                end = toIST(lastDayOfMonthUTC);
                setSelectedBtn('thisMonth');
                break;

            case 'last6Months':
                const firstDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() - 5, 1);
                const lastDayLast6UTC = new Date(todayUTC.getFullYear(), todayUTC.getMonth() + 1, 0);

                start = toIST(firstDayLast6UTC);
                end = toIST(lastDayLast6UTC);
                setSelectedBtn('last6Months');
                break;

            case 'thisYear':
                start = toIST(new Date(todayUTC.getFullYear(), 0, 1));
                end = toIST(new Date(todayUTC.getFullYear(), 11, 31));
                setSelectedBtn('thisYear');
                break;

            case 'lastYear':
                start = toIST(new Date(todayUTC.getFullYear() - 1, 0, 1));
                end = toIST(new Date(todayUTC.getFullYear() - 1, 11, 31));
                setSelectedBtn('lastYear');
                break;

            default:
                start = '';
                end = '';
                setSelectedBtn('range');
        }

        setStartDate(start);
        setEndDate(end);
        // keep picker drafts aligned with the quick-range commit so the Apply button doesn't flash
        setDraftStart(start ? dayjs(start) : null);
        setDraftEnd(end ? dayjs(end) : null);
    };

    // Commit the picked draft range — applies only on "Apply" click
    const handleApplyDateRange = () => {
        if (!draftStart || !draftStart.isValid() || !draftEnd || !draftEnd.isValid()) {
            toast.error('Please select both start and end dates', { autoClose: 2000 });
            return;
        }
        const today = dayjs().endOf('day');
        if (draftStart.isAfter(today) || draftEnd.isAfter(today)) {
            toast.error('Future dates are not allowed', { autoClose: 2000 });
            return;
        }
        if (draftStart.isAfter(draftEnd, 'day')) {
            toast.error('Start date cannot be after end date', { autoClose: 2000 });
            return;
        }
        setStartDate(draftStart.format('YYYY-MM-DD'));
        setEndDate(draftEnd.format('YYYY-MM-DD'));
        setSelectedBtn('range');
    };

    const handleDataTypeChange = (type) => {
        setSelectShowType(type);
    };

    const calculateTotalRevenue = () => {
        return planHistoryData.reduce((sum, plan) => sum + (plan.p_price || 0), 0);
    };

    const getCurrentData = () => {
        switch (selectShowType) {
            case 'users':
                return userData;
            case 'login':
                return loginData;
            case 'planHistory':
                return planHistoryData;
            case 'referral':
                return referralData;
            case 'referralPlan':
                return referralPlanData;
            case 'all':
                return [...userData, ...loginData, ...planHistoryData, ...referralData, ...referralPlanData];
            default:
                return [];
        }
    };

    const getNoDataMessage = () => {
        const messages = {
            users: { title: 'No Users Found', message: 'No user registrations found for the selected date range.' },
            login: { title: 'No Logins Found', message: 'No login records found for the selected date range.' },
            planHistory: { title: 'No Plan History', message: 'No plan purchase history found for the selected date range.' },
            referral: { title: 'No Referrals Found', message: 'No referral records found for the selected date range.' },
            referralPlan: { title: 'No Referral Plans', message: 'No referral plan history found for the selected date range.' },
            all: { title: 'No Data Available', message: 'No records found for the selected date range. Try adjusting your filters.' }
        };
        return messages[selectShowType] || messages.all;
    };

    const renderCharts = () => {
        const showUserChart = selectShowType === 'all' || selectShowType === 'users';
        const showLoginChart = selectShowType === 'all' || selectShowType === 'login';
        const showPlanHistoryChart = selectShowType === 'all' || selectShowType === 'planHistory';
        const showReferralChart = selectShowType === 'all' || selectShowType === 'referral';
        const showReferralPlanChart = selectShowType === 'all' || selectShowType === 'referralPlan';

        const chartSections = [];

        if (showUserChart && userData.length > 0) {
            chartSections.push({ type: 'user', component: <UserChart userData={userData} /> });
        }
        if (showLoginChart && androidLoginData.length > 0) {
            chartSections.push({ type: 'androidLogin', component: <LoginChart data={androidLoginData} title="Android Logins Over Time" /> });
        }
        if (showLoginChart && iosLoginData.length > 0) {
            chartSections.push({ type: 'iosLogin', component: <LoginChart data={iosLoginData} title="iOS Logins Over Time" /> });
        }
        if (showPlanHistoryChart && planHistoryData.length > 0) {
            chartSections.push({ type: 'planHistory', component: <PlanHistoryChart data={planHistoryData} /> });
        }
        if (showReferralChart && referralData.length > 0) {
            chartSections.push({ type: 'referral', component: <ReferralChart data={referralData} title="Referrals Over Time" /> });
        }
        if (showReferralPlanChart && referralPlanData.length > 0) {
            chartSections.push({ type: 'referralPlan', component: <ReferralChart data={referralPlanData} title="Referral Plans Over Time" /> });
        }

        if (chartSections.length === 0) {
            return null;
        }

        const isSingleChart = chartSections.length === 1;

        if (isSingleChart) {
            return (
                <div className="mt-6">
                    <div className="w-full max-w-4xl mx-auto">
                        {chartSections[0].component}
                    </div>
                </div>
            );
        }

        return (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-6 mt-6">
                {chartSections.map((chart) => (
                    <div key={chart.type}>
                        {chart.component}
                    </div>
                ))}
            </div>
        );
    };

    const renderCards = () => {
        const cardConfigs = [
            {
                show: selectShowType === 'all' || selectShowType === 'users',
                data: userData.length,
                label: 'Users',
                icon: <Users size={22} />,
                iconBg: 'bg-teal-50',
                iconColor: 'text-teal-600',
                onClick: () => navigate('/manage/user/all/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'login',
                data: loginData.length,
                label: 'Total Login',
                icon: <LogIn size={22} />,
                iconBg: 'bg-amber-50',
                iconColor: 'text-amber-600',
                onClick: () => navigate('/manage/user/login/all/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'login',
                data: androidLoginData.length,
                label: 'Android Login',
                icon: <Smartphone size={22} />,
                iconBg: 'bg-green-50',
                iconColor: 'text-green-600',
                onClick: () => navigate('/manage/user/login/android/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'login',
                data: iosLoginData.length,
                label: 'iOS Login',
                icon: <Apple size={22} />,
                iconBg: 'bg-blue-50',
                iconColor: 'text-blue-600',
                onClick: () => navigate('/manage/user/login/ios/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: planHistoryData.length,
                label: 'Total Plans',
                icon: <CreditCard size={22} />,
                iconBg: 'bg-purple-50',
                iconColor: 'text-purple-600',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: activePlans.length,
                label: 'Active Plans',
                icon: <BadgeCheck size={22} />,
                iconBg: 'bg-emerald-50',
                iconColor: 'text-emerald-600',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: expiredPlans.length,
                label: 'Expired Plans',
                icon: <XCircle size={22} />,
                iconBg: 'bg-red-50',
                iconColor: 'text-red-600',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'planHistory',
                data: `₹${calculateTotalRevenue()}`,
                label: 'Total Revenue',
                icon: <IndianRupee size={22} />,
                iconBg: 'bg-indigo-50',
                iconColor: 'text-indigo-600',
                onClick: () => navigate('/manage/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referral',
                data: referralData.length,
                label: 'Total Referrals',
                icon: <UsersRound size={22} />,
                iconBg: 'bg-cyan-50',
                iconColor: 'text-cyan-600',
                onClick: () => navigate('/manage/user/referral/all/data')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referralPlan',
                data: referralPlanData.length,
                label: 'Total Referral Plans',
                icon: <Gift size={22} />,
                iconBg: 'bg-orange-50',
                iconColor: 'text-orange-600',
                onClick: () => navigate('/manage/referral/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referralPlan',
                data: activeReferralPlans.length,
                label: 'Active Referral Plans',
                icon: <BadgeCheck size={22} />,
                iconBg: 'bg-lime-50',
                iconColor: 'text-lime-600',
                onClick: () => navigate('/manage/referral/plan/history')
            },
            {
                show: selectShowType === 'all' || selectShowType === 'referralPlan',
                data: expiredReferralPlans.length,
                label: 'Expired Referral Plans',
                icon: <XCircle size={22} />,
                iconBg: 'bg-rose-50',
                iconColor: 'text-rose-600',
                onClick: () => navigate('/manage/referral/plan/history')
            },
        ];

        const visibleCards = cardConfigs.filter(card => card.show);

        return (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                {visibleCards.map((card, index) => (
                    <div
                        key={index}
                        onClick={card.onClick}
                        className="group relative p-5 rounded-2xl cursor-pointer bg-main-white border border-main-border shadow-sm hover:shadow-lg hover:-translate-y-1 hover:border-main-blue/40 transition-all duration-300"
                    >
                        <div className="flex items-center justify-between gap-3">
                            <div className="min-w-0">
                                <p className="text-[11px] font-semibold uppercase tracking-wider text-main-subfont truncate">
                                    {card.label}
                                </p>
                                <h4 className="text-2xl sm:text-3xl font-bold tracking-tight mt-1.5 text-main-font">
                                    {card.data}
                                </h4>
                            </div>
                            <div className={`shrink-0 flex items-center justify-center rounded-xl p-3 ${card.iconBg} ${card.iconColor} transition-transform duration-300 group-hover:scale-110`}>
                                {card.icon}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    };

    const hasData = () => {
        const currentData = getCurrentData();
        return currentData.length > 0;
    };

    // Show the full skeleton only on the very first load (before any data is present)
    const initialLoading = loader && !hasData() && !dataFetchError;
    // Apply button appears only when the picker draft differs from the applied range
    const isRangeDirty =
        (draftStart && draftStart.isValid() ? draftStart.format('YYYY-MM-DD') : '') !== (startDate || '') ||
        (draftEnd && draftEnd.isValid() ? draftEnd.format('YYYY-MM-DD') : '') !== (endDate || '');

    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="w-full text-main-font"
        >
            {/* Header Section */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between pb-4 gap-3">
                <div className="flex items-center gap-3">
                    <div className="relative p-2.5 bg-main-blue rounded-xl shadow-lg shadow-main-blue/30 text-main-white">
                        <LayoutDashboard size={22} />
                        {deleteRequestCount > 0 && (
                            <button
                                onClick={() => navigate('/manage/user/all/delete')}
                                className="absolute -top-2 -right-2 min-w-[20px] h-5 px-1 bg-main-red text-white text-[10px] font-semibold rounded-full flex items-center justify-center shadow-md hover:opacity-90 transition-all duration-200"
                                title="View Delete Requests"
                            >
                                {deleteRequestCount > 99 ? '99+' : deleteRequestCount}
                            </button>
                        )}
                    </div>
                    <div>
                        <h1 className="text-2xl font-bold text-main-font">Dashboard</h1>
                        <p className="text-sm text-main-subfont">Overview of users, logins, plans & referrals</p>
                    </div>
                </div>

                {/* Actions - Last Updated & Refresh */}
                <div className="flex items-center gap-3">
                    <div className="bg-main-white px-4 py-2 rounded-xl shadow-sm border border-main-border text-sm text-main-subfont flex items-center gap-2">
                        <CalendarDays size={16} />
                        <span className="hidden sm:inline">Last updated:</span>
                        <span className="font-medium text-main-font">{lastUpdated.toLocaleTimeString()}</span>
                    </div>
                    <button
                        onClick={handleRefresh}
                        disabled={loader || isRefreshing}
                        className={`bg-main-blue hover:bg-main-blue/90 text-white p-2.5 rounded-xl shadow-sm border border-main-blue text-sm font-medium flex items-center gap-2 transition-all duration-200 ${(loader || isRefreshing) ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-md'}`}
                        title="Refresh Dashboard"
                    >
                        <RefreshCw className={`${(loader || isRefreshing) ? 'animate-spin' : ''}`} size={18} />
                    </button>
                </div>
            </div>

            {/* Filters Card */}
            <div className="bg-main-white rounded-2xl shadow-sm border border-main-border overflow-hidden">
                <div className="p-4 md:p-6">
                    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-5">
                        {/* Quick Range — modern segmented control */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarDays size={13} /> Quick Range
                            </p>
                            <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                                {QUICK_RANGES.map((r) => {
                                    const active = selectedBtn === r.key;
                                    return (
                                        <button
                                            key={r.key}
                                            onClick={() => handleDateRangeFilter(r.key)}
                                            className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                                ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                                : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                                }`}
                                        >
                                            {r.label}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Custom Range — themed MUI date pickers + Apply (applies only on click) */}
                        <div>
                            <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                                <CalendarRange size={13} /> Custom Range
                            </p>
                            <ThemeProvider theme={pickerTheme}>
                                <LocalizationProvider dateAdapter={AdapterDayjs}>
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2.5 bg-main-bg border border-main-border rounded-2xl p-2.5 shadow-sm">
                                        <DatePicker
                                            label="From"
                                            value={draftStart}
                                            onChange={(v) => setDraftStart(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            maxDate={draftEnd || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <span className="hidden sm:flex items-center justify-center text-main-subfont">
                                            <ArrowRight size={16} />
                                        </span>
                                        <DatePicker
                                            label="To"
                                            value={draftEnd}
                                            onChange={(v) => setDraftEnd(v)}
                                            format="DD MMM YYYY"
                                            disableFuture
                                            minDate={draftStart || undefined}
                                            slots={pickerSlots}
                                            slotProps={{ textField: { size: 'small', sx: dateFieldSx } }}
                                        />
                                        <Button
                                            variant="contained"
                                            onClick={handleApplyDateRange}
                                            sx={{ ...applyBtnSx, display: isRangeDirty ? 'inline-flex' : 'none' }}
                                            startIcon={<Check size={16} />}
                                        >
                                            Apply
                                        </Button>
                                    </div>
                                </LocalizationProvider>
                            </ThemeProvider>
                        </div>
                    </div>

                    {/* Applied-range indicator */}
                    {selectedBtn === 'range' && startDate && endDate && (
                        <div className="mt-3 inline-flex items-center gap-2 text-xs text-main-subfont bg-main-bg border border-main-border rounded-lg px-3 py-1.5">
                            <CalendarRange size={13} />
                            Showing <span className="font-semibold text-main-font">{dayjs(startDate).format('DD MMM YYYY')}</span>
                            <ArrowRight size={12} />
                            <span className="font-semibold text-main-font">{dayjs(endDate).format('DD MMM YYYY')}</span>
                        </div>
                    )}
                </div>

                {/* Data Type Filter — segmented control */}
                <div className="px-4 md:px-6 py-4 bg-main-white border-t border-main-border">
                    <p className="text-[11px] font-semibold text-main-subfont uppercase tracking-wider mb-2 flex items-center gap-1.5">
                        <SlidersHorizontal size={13} /> Data Type
                    </p>
                    <div className="inline-flex flex-wrap items-center gap-1 bg-main-bg border border-main-border rounded-xl p-1">
                        {DATA_TYPES.map((t) => {
                            const active = selectShowType === t.key;
                            return (
                                <button
                                    key={t.key}
                                    onClick={() => handleDataTypeChange(t.key)}
                                    className={`px-3.5 py-2 rounded-lg text-sm font-medium transition-all ${active
                                        ? 'bg-main-blue text-main-white shadow-sm shadow-main-blue/30'
                                        : 'text-main-subfont hover:text-main-font hover:bg-main-white'
                                        }`}
                                >
                                    {t.label}
                                </button>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Dashboard Content */}
            <div className="p-4 md:p-6 bg-main-white rounded-2xl border border-main-border shadow-sm mt-4">
                {dataFetchError ? (
                    <div className="bg-red-50 border border-red-200 text-red-700 px-6 py-4 rounded-xl shadow-sm flex items-center gap-3">
                        <AlertTriangle size={22} className="text-red-500 shrink-0" />
                        <span className="text-sm font-medium">{dataFetchError}</span>
                    </div>
                ) : initialLoading ? (
                    <div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <Skeleton key={i} variant="rounded" height={104} sx={{ borderRadius: '16px' }} />
                            ))}
                        </div>
                        <Skeleton variant="rounded" height={320} sx={{ borderRadius: '12px', mt: 3 }} />
                    </div>
                ) : hasData() ? (
                    <>
                        {renderCards()}
                        {renderCharts()}
                    </>
                ) : (
                    <div className="w-full flex flex-col items-center justify-center py-12 md:py-16 px-4">
                        <div className="bg-main-bg rounded-full p-5 mb-4 text-main-subfont">
                            <Inbox size={40} />
                        </div>
                        <h3 className="text-lg md:text-xl font-semibold text-main-font mb-2 text-center">
                            {getNoDataMessage().title}
                        </h3>
                        <p className="text-sm md:text-base text-main-subfont text-center max-w-md">
                            {getNoDataMessage().message}
                        </p>
                    </div>
                )}
            </div>
        </motion.div>
    );
};

export default Home;
